<template>
  <div class="content-insertion">
    <!-- 插入功能面板 -->
    <InsertPanel
      :visible.sync="insertPanelVisible"
      :features="features"
      @insert="handleInsert"
      @close="handleInsertPanelClose"
    />
  </div>
</template>

<script>
/**
 * ContentInsertion 内容插入器组件
 * 
 * 功能说明：
 * - 提供统一的内容插入入口，封装 InsertPanel 面板
 * - 支持配置化控制可用的插入功能类型
 * - 所有插入操作通过事件回调通知父组件执行
 * - 不直接管理组件渲染，由父组件负责组件的插入位置
 * 
 * 使用示例：
 * ```vue
 * <ContentInsertion
 *   :features="['image', 'highlight', 'columns', 'table']"
 *   @insert-image="handleImageInsert"
 *   @insert-highlight="handleHighlightInsert"
 *   @insert-columns="handleColumnsInsert"
 *   @insert-table="handleTableInsert"
 * />
 * ```
 * 
 * API 说明：
 * Props:
 * - features: Array, 可用功能列表
 * 
 * Events:
 * - insert-image(): 图片插入请求
 * - insert-highlight(defaultData): 高亮块插入请求，携带默认数据
 * - insert-columns(defaultData): 分栏布局插入请求，携带默认数据
 * - insert-table(defaultData): 表格插入请求，携带默认数据
 * - insert-file(defaultData): 文件插入请求，携带默认数据
 * - insert(type, defaultData): 通用插入请求，携带类型和默认数据
 * 
 * Methods:
 * - openPanel(): 打开插入面板
 * - closePanel(): 关闭插入面板
 */

import InsertPanel from './InsertPanel.vue'

export default {
  name: 'ContentInsertion',
  
  components: {
    InsertPanel
  },
  
  props: {
    /**
     * 可用功能列表
     * @type {Array}
     * @default ['image', 'highlight', 'columns', 'table']
     */
    features: {
      type: Array,
      default: () => ['image', 'highlight', 'columns', 'table', 'file']
    }
  },
  
  data() {
    return {
      insertPanelVisible: false
    }
  },
  
  methods: {
    /**
     * 打开插入面板
     */
    openPanel() {
      this.insertPanelVisible = true
    },
    
    /**
     * 关闭插入面板
     */
    closePanel() {
      this.insertPanelVisible = false
    },
    
    /**
     * 处理插入功能选择
     * @param {String} type - 功能类型标识
     */
    handleInsert(type) {
      let defaultData = null
      
      switch (type) {
        case 'image':
          this.$emit('insert-image')
          break
        case 'highlight':
          defaultData = this.createHighlightData()
          this.$emit('insert-highlight', defaultData)
          break
        case 'columns':
          defaultData = this.createColumnsData()
          this.$emit('insert-columns', defaultData)
          break
        case 'table':
          defaultData = this.createTableData()
          this.$emit('insert-table', defaultData)
          break
        case 'file':
          defaultData = this.createFileData()
          this.$emit('insert-file', defaultData)
          break
        default:
          console.warn(`未支持的插入类型: ${type}`)
          return
      }
      
      this.$emit('insert', type, defaultData)
    },
    
    /**
     * 处理插入面板关闭
     */
    handleInsertPanelClose() {
      this.insertPanelVisible = false
    },
    
    /**
     * 创建默认高亮块数据
     * @returns {Object}
     */
    createHighlightData() {
      return {
        text: '',
        bgColor: '#FFF3CD',
        borderColor: '#FFC107'
      }
    },
    
    /**
     * 创建默认分栏布局数据
     * @returns {Object}
     */
    createColumnsData() {
      return {
        columns: [
          {
            id: 1,
            content: '',
            width: 49.75,
            bgColor: '#EEF3FE',
            borderColor: '#EEF3FE',
            minHeight: 40
          },
          {
            id: 2,
            content: '',
            width: 49.75,
            bgColor: '#F7F3FF',
            borderColor: '#F7F3FF',
            minHeight: 40
          }
        ]
      }
    },
    
    /**
     * 创建默认表格数据
     * @returns {Object}
     */
    createTableData() {
      return {
        table: [
          [{content: '', bgColor: ''}, {content: '', bgColor: ''}, {content: '', bgColor: ''}],
          [{content: '', bgColor: ''}, {content: '', bgColor: ''}, {content: '', bgColor: ''}],
          [{content: '', bgColor: ''}, {content: '', bgColor: ''}, {content: '', bgColor: ''}]
        ],
        rowBgColors: {},
        colBgColors: {},
        cellBgColors: {},
        isFullWidth: false,
        hasHeaderRow: true,
        hasHeaderCol: false
      }
    },
    
    /**
     * 创建默认文件数据
     * @returns {Object}
     */
    createFileData() {
      return {
        fileId: '',
        fileName: '',
        fileUrl: ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
</style>



// WEBPACK FOOTER //
// src/components/module/course/public/ContentInsertion.vue